import React, { useState } from "react";
import { Box } from "@mui/material";
import SearchBox from "components/SearchBox";
import { styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import Notify from "config/notify";

interface FileUploadInputProps {
  label: string;
  sx?: object;
  onFileChange?: (file: File | null) => void;
  latestDate?: string;
  url?: string;
  fileAccept?: string;
}

const ButtonCustom = styled("label")(() => ({
  fontWeight: "medium",
  color: "var(--gray-700)",
  border: "1px solid var(--gray-250)",
  height: "44px",
  borderRadius: "10px",
  boxShadow: "0px 3px 6px var(--shadow-sm-overlay)",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  paddingLeft: "22px",
  cursor: "pointer",
  minWidth: "200px",
  ":hover": {
    border: "1px solid var(--info)",
    color: "var(--info)",
  },
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const StyledLink = styled(RouterLink)({
  fontWeight: "medium",
  color: "var(--gray-600)",
  textDecoration: "underline",
});

const DragDropArea = styled(Box)(({ theme }) => ({
  width: "180px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  border: "1px solid var(--gray-600)",
  height: "48px",
  lineHeight: "48px",
  padding: "0 10px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: "var(--info)",
    backgroundColor: "rgba(0, 123, 255, 0.05)",
  },
  "&.drag-over": {
    borderColor: "var(--info)",
    backgroundColor: "rgba(0, 123, 255, 0.1)",
    boxShadow: "0 0 0 1px var(--info)",
  },
}));

/**
 * FileUploadInput component allows users to upload a file and displays the file name and upload time.
 *
 * @param {Object} props - The component props.
 * @param {string} props.label - The label for the file input.
 * @param {Object} [props.sx] - The style object for the component.
 * @param {Function} [props.onFileChange] - The callback function to handle file change events.
 * @returns {JSX.Element} The rendered FileUploadInput component.
 */
function FileUploadInput({
  label,
  sx,
  onFileChange,
  latestDate,
  url,
  fileAccept,
}: FileUploadInputProps): JSX.Element {
  const [isDragOver, setIsDragOver] = useState<boolean>(false);

  const handleFileChange = (file: File | null) => {
    if (file) {
      if (fileAccept && !fileAccept.split(",").includes(file.type)) {
        Notify.setAlert({
          severity: "error",
          message: "ファイル形式が正しくありません。",
          show: true,
        });
        return;
      }

      if (onFileChange) {
        onFileChange(file);
      }
    }
  };

  const handleInputFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;
    handleFileChange(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const { files } = e.dataTransfer;
    if (files.length > 0) {
      handleFileChange(files[0]);
    }
  };

  const handleDragDropClick = () => {
    // Trigger file input when drag drop area is clicked
    const fileInput = document.getElementById("file-input") as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      columnGap={{ xs: "10px", lg: "150px" }}
      rowGap="10px"
      marginBottom="42px"
      flexWrap="wrap"
      flex={1}
      sx={{ width: { xs: "100%", sm: "auto" }, ...sx }}
    >
      <Box display="flex" alignItems="center" columnGap="10px" rowGap="10px" flexWrap="wrap">
        <SearchBox
          title={label}
          placeholder={label}
          id="1"
          readOnly
          value=""
          sx={{
            minWidth: "475px",
            flexGrow: { xs: 1, lg: 0 },
          }}
          sxTextField={{
            flexGrow: 1,
            width: "239px",
          }}
          sxLabel={{
            width: "192px",
            fontSize: { xs: 16, md: 18, lg: 20 },
          }}
        >
          <Box sx={{ position: "relative" }}>
            <DragDropArea
              className={isDragOver ? "drag-over" : ""}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleDragDropClick}
            >
              ドラッグ＆ドロップ
            </DragDropArea>
          </Box>
        </SearchBox>

        <ButtonCustom
          sx={{
            minWidth: { lg: "238px" },
            fontSize: { xs: 16, md: 18, lg: 22 },
          }}
        >
          ファイルアップロード
          <VisuallyHiddenInput
            id="file-input"
            type="file"
            onChange={handleInputFileChange}
            multiple
          />
        </ButtonCustom>
      </Box>

      {latestDate && url && (
        <Box
          sx={{
            minWidth: { lg: "300px" },
            textDecoration: "underline",
            whiteSpace: "nowrap",
            fontSize: { xs: 16, md: 18, lg: 20 },
          }}
        >
          <StyledLink to={url}>最終アップロード {latestDate}</StyledLink>
        </Box>
      )}
    </Box>
  );
}

export default FileUploadInput;
